import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ConfirmModal from "../../components/ConfirmModal";
import * as Fonts from "../../components/Fonts";
import { LoadingModal } from "../../components/LoadingModal";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { ScreenHeader } from "../../components/SubHeader";
import { useAccountCurrency } from "../../hooks/AccountCurrency";
import ChevronRightIcon from "../../icons/ChevronRight";
import { api, ApiSiteResponse, hasActiveSubscription, User } from "../../services/api";
import { signOut } from "../../services/auth";
import { Language, LANGUAGES, toLanguage } from "../../services/i18ln/languages";
import { currenciesIn } from "../Editor/pages/Pricing/components/CurrencySelect";
import { CurrencyModal } from "./CurrencyModal";
import { CurrencyIcon } from "./icons/Currency";
import { LANGUAGE_FLAGS } from "./icons/Flags";
import { LangIcon } from "./icons/Lang";
import { LogoutIcon } from "./icons/Logout";
import { FeedbackIcon, HelpIcon } from "./icons/Support";
import { Card, Container, Footer, LangOptions, List, MobileOnlySection, PlusKeepos, Profile, Section } from "./style";

const labelOf = (code: Language) => LANGUAGES.find(language => language.code === code)?.label;

export const AccountPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLang = useRef<Language>(toLanguage(i18n.language));

  const [isLoading, setLoading] = useState(true);
  const [user, setUser] = useState<User>();

  const [isShowLangModal, setShowLangModal] = useState(false);
  // The language marked in the modal: it only switches on Select
  const [pickedLang, setPickedLang] = useState<Language>(currentLang.current);
  const [isConfirmingLogout, setConfirmingLogout] = useState(false);
  // The prices' currency (the agenda's; a new Pricing section's): chosen here, else guessed from the browser
  const accountCurrency = useAccountCurrency();
  const [isShowCurrencyModal, setShowCurrencyModal] = useState(false);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [currencyError, setCurrencyError] = useState(false);
  const currencyLabel = (code: string) => {
    const found = currenciesIn(i18n.language).find(currency => currency.code === code);
    return found ? `${found.name} (${found.symbol})` : code;
  };
  const selectCurrency = async (code: string) => {
    setSavingCurrency(true);
    setCurrencyError(false);
    try {
      await accountCurrency.save(code);
      setShowCurrencyModal(false);
    } catch {
      setCurrencyError(true);
    } finally {
      setSavingCurrency(false);
    }
  };
  // Deleting the account: Keepos with a PLUS subscription still charging block it (they're listed to cancel first)
  const [deleteModal, setDeleteModal] = useState<{ plusSites: ApiSiteResponse[] }>();

  const openDeleteModal = async () => {
    const sites = await api.getSites().catch(() => [] as ApiSiteResponse[]);
    setDeleteModal({
      plusSites: sites.filter(({ subscription }) => hasActiveSubscription(subscription) && !subscription?.canceled),
    });
  };

  const openLangModal = () => {
    setPickedLang(currentLang.current);
    setShowLangModal(true);
  };

  const loadUser = async () => {
    setUser(await api.getUser());
    setLoading(false);
  };

  // Picking the current language just closes the modal; the other one is saved, then the editor switches to it. A
  // failure keeps the language it had and says so (so the account and the editor don't disagree), and a second
  // choice waits for the first
  const savingLanguage = useRef(false);
  const [languageError, setLanguageError] = useState(false);
  const selectLang = useCallback(
    async (lang: Language) => {
      if (savingLanguage.current) {
        return;
      }
      setShowLangModal(false);
      if (lang === currentLang.current) {
        return;
      }
      savingLanguage.current = true;
      setLanguageError(false);
      setLoading(true);
      try {
        await api.updateLang(lang.toUpperCase());
        await i18n.changeLanguage(lang);
        currentLang.current = lang;
      } catch {
        setLanguageError(true);
      } finally {
        savingLanguage.current = false;
        setLoading(false);
      }
    },
    [i18n]
  );

  useEffect(() => {
    loadUser();
  }, []);

  return isLoading ? (
    <LoadingModal />
  ) : (
    <Container>
      <ScreenHeader title={t("pages.account.header.title")} hideOnMobile />

      <Section>
        {languageError && (
          <p className="typography-p text-red-500" role="alert">
            {t("pages.account.errors.language")}
          </p>
        )}
        <Fonts.Label>{t("pages.account.sections.profile")}</Fonts.Label>
        <Card>
          <Profile>
            <span className="avatar" aria-hidden="true">
              {user?.name?.trim().charAt(0) || "K"}
            </span>
            <div className="text">
              <h6>{user?.name}</h6>
              <p>{user?.email}</p>
            </div>
          </Profile>
        </Card>
      </Section>

      <Section>
        <Fonts.Label>{t("pages.account.sections.preferences")}</Fonts.Label>
        <List>
          <li>
            <button type="button" onClick={openLangModal}>
              <LangIcon />
              <span className="title">{t("pages.account.actions.lang.title")}</span>
              <span className="value">{labelOf(currentLang.current)}</span>
              <span className="chevron">
                <ChevronRightIcon width={14} height={14} color="currentColor" />
              </span>
            </button>
          </li>
          <li>
            <button type="button" onClick={() => setShowCurrencyModal(true)}>
              <CurrencyIcon />
              <span className="title">{t("pages.account.actions.currency.title")}</span>
              <span className="value">{currencyLabel(accountCurrency.currency)}</span>
              <span className="chevron">
                <ChevronRightIcon width={14} height={14} color="currentColor" />
              </span>
            </button>
          </li>
        </List>
      </Section>

      {/* Phones only: the bottom nav has no room for Ajuda and Feedback, the desktop sidebar does */}
      <MobileOnlySection>
        <Fonts.Label>{t("pages.account.sections.support")}</Fonts.Label>
        <List>
          <li>
            <button type="button" onClick={() => navigate("/help")}>
              <HelpIcon />
              <span className="title">{t("components.appShell.nav.help")}</span>
              <span className="chevron">
                <ChevronRightIcon width={14} height={14} color="currentColor" />
              </span>
            </button>
          </li>
          <li>
            <button type="button" onClick={() => navigate("/feedback")}>
              <FeedbackIcon />
              <span className="title">{t("components.appShell.nav.feedback")}</span>
              <span className="chevron">
                <ChevronRightIcon width={14} height={14} color="currentColor" />
              </span>
            </button>
          </li>
        </List>
      </MobileOnlySection>

      {/* Signing out, on its own */}
      <Section>
        <List>
          <li>
            <button type="button" onClick={() => setConfirmingLogout(true)}>
              <LogoutIcon />
              <span className="title">{t("actions.logout")}</span>
            </button>
          </li>
        </List>
      </Section>

      <Footer>
        <a href="https://www.keepo.bio/termos-de-uso/" target="_blank">
          {t("pages.account.actions.terms")}
        </a>
        <a href="https://www.keepo.bio/politica-de-privacidade/" target="_blank">
          {t("pages.account.actions.privacy")}
        </a>
        {/* small, as the links beside it: deleting the account is there, without the weight of a block */}
        <button type="button" className="danger" onClick={openDeleteModal}>
          {t("pages.account.deleteAccount.button")}
        </button>
      </Footer>

      {deleteModal &&
        (deleteModal.plusSites.length ? (
          // A PLUS subscription would keep charging a deleted account: it has to be cancelled first
          <Modal
            outsideExit
            onExit={() => setDeleteModal(undefined)}
            title={t("pages.account.deleteAccount.plusModal.title")}
            subtitle={t("pages.account.deleteAccount.plusModal.description")}
            footer={
              <ModalButton onClick={() => setDeleteModal(undefined)}>
                {t("pages.account.deleteAccount.plusModal.close")}
              </ModalButton>
            }>
            <PlusKeepos>
              {deleteModal.plusSites.map(({ siteId, site, draft }) => (
                <li key={siteId}>
                  <span className="keepo">{(site || draft)?.title}</span>
                  <button type="button" onClick={() => navigate(`/subscription/cancel?site=${siteId}`)}>
                    {t("pages.account.deleteAccount.plusModal.cancelPlus")}
                  </button>
                </li>
              ))}
            </PlusKeepos>
          </Modal>
        ) : (
          <ConfirmModal
            type="DANGER"
            title={t("pages.account.deleteAccount.confirmModal.title")}
            descriptions={[t("pages.account.deleteAccount.confirmModal.description")]}
            confirmButtonTitle={t("pages.account.deleteAccount.confirmModal.continue")}
            cancelTitle={t("pages.account.deleteAccount.confirmModal.cancel")}
            onConfirm={() => navigate("/account/delete")}
            onCancel={() => setDeleteModal(undefined)}
          />
        ))}

      {isConfirmingLogout && (
        <ConfirmModal
          type="DANGER"
          title={t("pages.account.actions.logout.confirmModal.title")}
          confirmButtonTitle={t("pages.account.actions.logout.confirmModal.confirm")}
          cancelTitle={t("pages.account.actions.logout.confirmModal.cancel")}
          onConfirm={() => signOut()}
          onCancel={() => setConfirmingLogout(false)}
          onExit={() => setConfirmingLogout(false)}
        />
      )}

      {isShowCurrencyModal && (
        <CurrencyModal
          current={accountCurrency.currency}
          saving={savingCurrency}
          error={currencyError ? t("pages.account.actions.currency.error") : undefined}
          onSelect={selectCurrency}
          onExit={() => setShowCurrencyModal(false)}
        />
      )}

      {isShowLangModal && (
        <Modal
          size="sm"
          outsideExit
          onExit={() => setShowLangModal(false)}
          title={t("pages.account.actions.lang.modalTitle")}
          subtitle={t("pages.account.actions.lang.modalSubtitle")}
          footer={
            <>
              <ModalSecondaryButton type="button" onClick={() => setShowLangModal(false)}>
                {t("actions.cancel")}
              </ModalSecondaryButton>
              <ModalButton disabled={pickedLang === currentLang.current} onClick={() => selectLang(pickedLang)}>
                {t("pages.account.actions.lang.select")}
              </ModalButton>
            </>
          }>
          <LangOptions role="radiogroup" aria-label={t("pages.account.actions.lang.modalTitle")}>
            {LANGUAGES.map(({ code: lang, label }) => {
              const Flag = LANGUAGE_FLAGS[lang];
              const isPicked = lang === pickedLang;
              return (
                <li key={lang}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isPicked}
                    className={isPicked ? "selected" : undefined}
                    onClick={() => setPickedLang(lang)}>
                    <Flag square />
                    <span className="text">
                      <b>{label}</b>
                      <span>{t(`pages.account.actions.lang.names.${lang}`)}</span>
                    </span>
                    {lang === currentLang.current && (
                      <span className="current">{t("pages.account.actions.lang.current")}</span>
                    )}
                    <span className="radio" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </LangOptions>
        </Modal>
      )}
    </Container>
  );
};
